import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { getSocket } from '../services/socket';

// Internal team chat — one implementation shared by the admin and agent apps
// (the only difference between the two copies is ADMIN_APP below).
//
//   Announce  admin/supervisor broadcast to everyone in the account
//   Campaign  a campaign's team room (admin picks one; an agent uses their own)
//   Direct    1:1 messages
//
// What makes it dependable on a call-centre floor:
//   • Unread counts come from the server (/chat/unread), so they survive a
//     refresh, a logout or a message that arrived while the person was away.
//   • Every tab, campaign and contact carries its own unread badge.
//   • A message is shown as "Sending…" until the server confirms it was saved;
//     if that does not happen it is marked "Not sent" with Retry.
//   • After a connection drop the open conversation and the counts are reloaded.
//   • Direct messages show "Seen" once the other person has opened them.
//   • Admins can remove a message for everyone.
// It rides the existing socket and never touches calls or Asterisk.
const ADMIN_APP = false;
const EMOJIS = ['😀','😂','😊','😉','😍','🤔','😅','😢','😡','👍','👎','👏','🙏','💪','🔥','🎉','✅','❌','⚠️','⭐','❤️','💯','☎️','📞','📋','📝','💬','⏰','☕','🍕'];
const TOKEN_KEY = ADMIN_APP ? 'admin_token' : 'agent_token';
const USER_KEY = ADMIN_APP ? 'admin_user' : 'agent_user';
const PAGE = 100;
const SEND_TIMEOUT_MS = 8000;

const ACCENT = '#0284c7';
const PANEL_BG = '#ffffff';
const CARD_BG = '#e8f1fe';
const BORDER = '#d6e3f5';
const TEXT = '#0b1b3a';
const TEXT2 = '#4a5f85';
const RED = '#e11d48';

const sum = o => Object.values(o || {}).reduce((a, x) => a + (Number(x) || 0), 0);
const dayKey = ts => { const d = new Date(ts); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
const dayLabel = ts => {
  const d = new Date(ts), now = new Date();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (dayKey(d) === dayKey(now)) return 'Today';
  if (dayKey(d) === dayKey(y)) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
};
const fmtTime = ts => { try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch (_) { return ''; } };
const fmtShort = ts => { if (!ts) return ''; return dayKey(ts) === dayKey(new Date()) ? fmtTime(ts) : new Date(ts).toLocaleDateString([], { day: 'numeric', month: 'short' }); };

export default function ChatWidget({ user: userProp, campaignId, campaignName }) {
  const user = userProp || (() => { try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch (_) { return null; } })();
  const myId = user?.id;
  const isAdmin = ['admin', 'supervisor', 'super_admin'].includes(user?.role);

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('broadcast');          // broadcast | campaign | dm
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [users, setUsers] = useState([]);
  const [dmUser, setDmUser] = useState(null);
  const [campList, setCampList] = useState([]);
  const [campSel, setCampSel] = useState('');           // admin: campaign chosen in the dropdown
  const [text, setText] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);   // emoji picker (2026-10-08)
  const [search, setSearch] = useState('');
  const [unread, setUnread] = useState({ broadcast: 0, campaigns: {}, dms: {} });
  const [connected, setConnected] = useState(true);
  const [outbox, setOutbox] = useState([]);             // messages awaiting the server's confirmation
  const [peerRead, setPeerRead] = useState(0);          // highest message id the DM partner has read
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('chat_sound') !== '0');
  const [priority, setPriority] = useState(false);      // admin: send as a pop-up
  const [popups, setPopups] = useState([]);             // agent: priority messages on screen
  const [shake, setShake] = useState(false);
  const [pendingAtt, setPendingAtt] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [toasts, setToasts] = useState([]);              // "who just wrote" cards while the panel is closed

  const bodyRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const audioCtxRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  const campId = ADMIN_APP ? campSel : campaignId;
  const token = () => localStorage.getItem(TOKEN_KEY);
  const isAuthed = !!token() && !!myId;

  // The listeners below are attached once; they read the current view from here.
  const viewRef = useRef({});
  viewRef.current = { open, tab, dmUser, campId };
  const viewKey = `${tab}:${tab === 'dm' ? (dmUser ? dmUser.id : '') : tab === 'campaign' ? (campId || '') : ''}`;

  const scrollDown = () => { setTimeout(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, 40); };

  const beep = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      const tone = (freq, t0, dur, peak) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, now + t0);
        g.gain.exponentialRampToValueAtTime(peak, now + t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, now + t0 + dur);
        o.connect(g); g.connect(ctx.destination);
        o.start(now + t0); o.stop(now + t0 + dur + 0.03);
      };
      tone(784, 0, 0.18, 0.32);
      tone(1047, 0.12, 0.24, 0.32);
    } catch (_) {}
  }, []);
  const toggleSound = () => setSoundOn(v => { const nv = !v; localStorage.setItem('chat_sound', nv ? '1' : '0'); return nv; });

  const inView = (m, v) => {
    if (v.tab === 'broadcast') return m.channel === 'broadcast';
    if (v.tab === 'campaign') return m.channel === 'campaign' && String(m.campaign_id) === String(v.campId);
    if (v.tab === 'dm' && v.dmUser) return m.channel === 'dm' && (
      (m.sender_id === myId && m.recipient_id === v.dmUser.id) || (m.sender_id === v.dmUser.id && m.recipient_id === myId));
    return false;
  };

  // ── Server-side unread counts ──────────────────────────────────────────────
  const loadUnread = useCallback(async () => {
    try {
      const d = await api.get('/chat/unread', { params: ADMIN_APP ? {} : { campaign_id: campaignId || undefined } });
      if (d && typeof d === 'object') setUnread({ broadcast: d.broadcast || 0, campaigns: d.campaigns || {}, dms: d.dms || {} });
    } catch (_) {}
  }, [campaignId]);

  const markRead = useCallback(async (v) => {
    const body = v.tab === 'dm' ? (v.dmUser ? { channel: 'dm', with: v.dmUser.id } : null)
      : v.tab === 'campaign' ? (v.campId ? { channel: 'campaign', campaign_id: v.campId } : null)
      : { channel: 'broadcast' };
    if (!body) return;
    setUnread(u => {
      if (body.channel === 'broadcast') return { ...u, broadcast: 0 };
      if (body.channel === 'campaign') return { ...u, campaigns: { ...u.campaigns, [body.campaign_id]: 0 } };
      return { ...u, dms: { ...u.dms, [body.with]: 0 } };
    });
    if (body.channel === 'dm') setUsers(list => list.map(x => (x.id === body.with ? { ...x, unread: 0 } : x)));
    try { await api.post('/chat/read', body); } catch (_) {}
  }, []);

  const loadUsers = useCallback(async () => {
    try { const d = await api.get('/chat/users'); setUsers(Array.isArray(d) ? d : []); } catch (_) {}
  }, []);

  // ── History ────────────────────────────────────────────────────────────────
  const historyParams = (v) => {
    const p = { channel: v.tab, limit: PAGE };
    if (v.tab === 'dm') { if (!v.dmUser) return null; p.with = v.dmUser.id; }
    if (v.tab === 'campaign') { if (!v.campId) return null; p.campaign_id = v.campId; }
    return p;
  };
  const loadHistory = useCallback(async () => {
    const v = viewRef.current;
    const p = historyParams(v);
    if (!p) { setMessages([]); setHasMore(false); return; }
    try {
      const data = await api.get('/chat/history', { params: p });
      const list = Array.isArray(data) ? data : [];
      setMessages(list); setHasMore(list.length >= PAGE);
      scrollDown();
      markRead(v);
      if (v.tab === 'dm' && v.dmUser) {
        try { const s = await api.get('/chat/seen', { params: { with: v.dmUser.id } }); setPeerRead(Number(s && s.last_read_id) || 0); } catch (_) {}
      } else setPeerRead(0);
    } catch (_) { setMessages([]); setHasMore(false); }
  }, [markRead]);

  const loadEarlier = async () => {
    const v = viewRef.current; const p = historyParams(v);
    if (!p || !messages.length) return;
    try {
      const el = bodyRef.current; const prevH = el ? el.scrollHeight : 0;
      const older = await api.get('/chat/history', { params: { ...p, before: messages[0].id } });
      const list = Array.isArray(older) ? older : [];
      setHasMore(list.length >= PAGE);
      setMessages(cur => [...list, ...cur]);
      setTimeout(() => { if (el) el.scrollTop = el.scrollHeight - prevH; }, 40);
    } catch (_) {}
  };

  // ── Live events (attached once) ────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthed) return;
    const s = getSocket();
    setConnected(!!s.connected);
    const onMsg = (m) => {
      const v = viewRef.current;
      const visible = v.open && inView(m, v);
      if (visible) {
        setMessages(prev => (prev.some(x => x.id === m.id) ? prev : [...prev, m]));
        scrollDown();
      }
      if (m.channel === 'dm') {
        const other = m.sender_id === myId ? m.recipient_id : m.sender_id;
        setUsers(list => list.map(x => (x.id === other ? {
          ...x, last_id: m.id, last_at: m.created_at, last_mine: m.sender_id === myId,
          last_body: m.body || (m.attachment_name ? `Attachment: ${m.attachment_name}` : ''),
          unread: m.sender_id !== myId && !visible ? (x.unread || 0) + 1 : x.unread
        } : x)));
      }
      if (m.sender_id === myId) return;
      if (soundOnRef.current) beep();
      if (!ADMIN_APP && m.priority) setPopups(p => (p.some(x => x.id === m.id) ? p : [...p, m]));
      if (visible) { markRead(v); return; }
      setUnread(u => {
        if (m.channel === 'broadcast') return { ...u, broadcast: u.broadcast + 1 };
        if (m.channel === 'campaign') return { ...u, campaigns: { ...u.campaigns, [m.campaign_id]: (u.campaigns[m.campaign_id] || 0) + 1 } };
        return { ...u, dms: { ...u.dms, [m.sender_id]: (u.dms[m.sender_id] || 0) + 1 } };
      });
      // Panel closed: say who it is from, so nobody has to open every chat to
      // find the new message. (Priority messages already get their own pop-up.)
      if (!v.open && !(m.priority && !ADMIN_APP)) {
        setToasts(t => [...t.filter(x => x.id !== m.id), m].slice(-3));
        setTimeout(() => setToasts(t => t.filter(x => x.id !== m.id)), 9000);
      }
      setShake(true); setTimeout(() => setShake(false), 600);
    };
    const onDeleted = (ev) => {
      setMessages(prev => prev.filter(x => x.id !== ev.id));
      setPopups(p => p.filter(x => x.id !== ev.id));
      setToasts(t => t.filter(x => x.id !== ev.id));
      loadUnread();
    };
    const onRead = (ev) => {
      const v = viewRef.current;
      if (v.tab === 'dm' && v.dmUser && ev.by === v.dmUser.id) setPeerRead(r => Math.max(r, Number(ev.last_read_id) || 0));
    };
    // After a drop: counts and the open conversation are reloaded so nothing sent
    // in the gap is missed.
    const onConnect = () => { setConnected(true); loadUnread(); if (viewRef.current.open) { loadHistory(); loadUsers(); } };
    const onDisconnect = () => setConnected(false);
    s.on('chat:message', onMsg); s.on('chat:deleted', onDeleted); s.on('chat:read', onRead);
    s.on('connect', onConnect); s.on('disconnect', onDisconnect);
    return () => {
      s.off('chat:message', onMsg); s.off('chat:deleted', onDeleted); s.off('chat:read', onRead);
      s.off('connect', onConnect); s.off('disconnect', onDisconnect);
    };
  }, [isAuthed, myId]);

  // An agent sits in their campaign's team room; joined again after every
  // reconnect, because a new connection starts outside all rooms.
  useEffect(() => {
    if (ADMIN_APP || !isAuthed || !campaignId) return;
    const s = getSocket();
    const join = () => s.emit('chat:join', { campaign_id: campaignId });
    join(); s.on('connect', join);
    return () => s.off('connect', join);
  }, [isAuthed, campaignId]);

  // Counts on sign-in, when the agent's campaign changes, and as a slow safety net.
  useEffect(() => {
    if (!isAuthed) return;
    loadUnread(); loadUsers();
    if (ADMIN_APP) api.get('/chat/campaigns').then(d => setCampList(Array.isArray(d) ? d : [])).catch(() => {});
    const iv = setInterval(loadUnread, 60000);
    return () => clearInterval(iv);
  }, [isAuthed, loadUnread, loadUsers]);

  useEffect(() => { if (open && isAuthed) loadHistory(); }, [open, viewKey, isAuthed, loadHistory]);
  useEffect(() => {
    if (!open || !isAuthed) return;
    loadUsers();
    if (ADMIN_APP) api.get('/chat/campaigns').then(d => setCampList(Array.isArray(d) ? d : [])).catch(() => {});
  }, [open, isAuthed, loadUsers]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open, viewKey]);

  // ── Totals ─────────────────────────────────────────────────────────────────
  const campUnread = ADMIN_APP ? sum(unread.campaigns) : (unread.campaigns[campaignId] || 0);
  const dmUnread = sum(unread.dms);
  const total = (unread.broadcast || 0) + campUnread + dmUnread;

  // The browser tab shows the count too, for when the window is in the background.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, '');
    document.title = total > 0 ? `(${total > 99 ? '99+' : total}) ${base}` : base;
  }, [total]);

  // ── Sending ────────────────────────────────────────────────────────────────
  const dispatch = (entry) => {
    let settled = false;
    const fail = (why) => { if (settled) return; settled = true; setOutbox(o => o.map(x => (x.cid === entry.cid ? { ...x, state: 'failed', error: why } : x))); };
    const timer = setTimeout(() => fail('No reply from the server'), SEND_TIMEOUT_MS);
    try {
      getSocket().emit('chat:send', entry.payload, (res) => {
        if (settled) return;
        clearTimeout(timer);
        if (res && res.ok) { settled = true; setOutbox(o => o.filter(x => x.cid !== entry.cid)); }
        else fail((res && res.error) || 'Not sent');
      });
    } catch (_) { clearTimeout(timer); fail('Not sent'); }
  };
  const send = () => {
    const body = text.trim();
    if (!body && !pendingAtt) return;
    if (tab === 'dm' && !dmUser) return;
    if (tab === 'broadcast' && !isAdmin) return;
    if (tab === 'campaign' && !campId) return;
    const entry = {
      cid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, view: viewKey, state: 'sending', at: new Date().toISOString(),
      payload: {
        channel: tab,
        recipient_id: tab === 'dm' ? dmUser.id : undefined,
        campaign_id: tab === 'campaign' ? campId : undefined,
        body,
        priority: ADMIN_APP && priority ? 1 : 0,
        attachment: pendingAtt || undefined
      }
    };
    setOutbox(o => [...o, entry]);
    setText(''); setPendingAtt(null);
    scrollDown();
    dispatch(entry);
  };
  const retry = (entry) => { setOutbox(o => o.map(x => (x.cid === entry.cid ? { ...x, state: 'sending', error: null } : x))); dispatch(entry); };
  const discard = (entry) => setOutbox(o => o.filter(x => x.cid !== entry.cid));

  const removeMessage = async (m) => {
    if (!window.confirm('Remove this message for everyone?')) return;
    try { await api.delete(`/chat/messages/${m.id}`); setMessages(prev => prev.filter(x => x.id !== m.id)); }
    catch (e) { alert((e && e.error) || 'Could not remove the message'); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setAttaching(true);
    try {
      const form = new FormData(); form.append('file', file);
      const r = await api.post('/chat/upload', form);
      if (r && r.url) setPendingAtt(r);
    } catch (e) { alert((e && e.error) || 'Upload failed (images, PDF, Word, Excel or text up to 10MB)'); }
    finally { setAttaching(false); }
  };
  const onPaste = (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (!items) return;
    for (const it of items) {
      if (it.type && it.type.indexOf('image') === 0) {
        const file = it.getAsFile();
        if (file) { e.preventDefault(); uploadFile(file); return; }
      }
    }
  };

  // A pop-up or "who wrote" card → open the conversation it belongs to.
  const openInChat = (p) => {
    setOpen(true);
    if (p.channel === 'dm') { setTab('dm'); setDmUser({ id: p.sender_id, name: p.sender_name, role: p.sender_role }); }
    else if (p.channel === 'campaign') { setTab('campaign'); if (ADMIN_APP && p.campaign_id) setCampSel(String(p.campaign_id)); }
    else setTab('broadcast');
    setPopups(list => list.filter(x => x.id !== p.id));
    setToasts([]);
  };

  // Opening the chat lands on whatever is unread: one person wrote → their
  // conversation; several → the Direct list (unread ones on top, highlighted);
  // otherwise the campaign room or the announcements that have something new.
  const toggleOpen = () => {
    if (open) { setOpen(false); return; }
    const dmIds = Object.keys(unread.dms).filter(k => unread.dms[k] > 0);
    if (dmIds.length === 1) {
      const u = users.find(x => String(x.id) === String(dmIds[0]));
      setTab('dm'); setDmUser(u || { id: Number(dmIds[0]), name: 'Direct message' });
    } else if (dmIds.length > 1) { setTab('dm'); setDmUser(null); }
    else if (campUnread > 0) {
      setTab('campaign');
      if (ADMIN_APP) { const c = Object.keys(unread.campaigns).find(k => unread.campaigns[k] > 0); if (c) setCampSel(String(c)); }
    } else if ((unread.broadcast || 0) > 0) setTab('broadcast');
    setToasts([]);
    setOpen(true);
  };

  if (!isAuthed) return null;

  // Where the unread messages are, named: shown beside the launcher while the
  // panel is closed, so the count is never just an anonymous number. One line
  // per conversation; clicking a line opens exactly that conversation.
  const whereUnread = [];
  Object.keys(unread.dms).forEach(k => {
    const n = unread.dms[k]; if (!n) return;
    const u = users.find(x => String(x.id) === String(k));
    whereUnread.push({ key: `dm:${k}`, kind: 'Direct', name: u ? u.name : 'Direct message', n,
      go: () => { setTab('dm'); setDmUser(u || { id: Number(k), name: 'Direct message' }); } });
  });
  if (ADMIN_APP) {
    Object.keys(unread.campaigns).forEach(k => {
      const n = unread.campaigns[k]; if (!n) return;
      const c = campList.find(x => String(x.id) === String(k));
      whereUnread.push({ key: `c:${k}`, kind: 'Campaign', name: c ? c.name : 'Campaign team', n,
        go: () => { setTab('campaign'); setCampSel(String(k)); } });
    });
  } else if (campUnread > 0) {
    whereUnread.push({ key: 'c:mine', kind: 'Campaign', name: campaignName || 'Campaign team', n: campUnread, go: () => setTab('campaign') });
  }
  if ((unread.broadcast || 0) > 0) {
    whereUnread.push({ key: 'b', kind: 'Announce', name: 'Announcement', n: unread.broadcast, go: () => setTab('broadcast') });
  }

  const tabs = [
    { key: 'broadcast', label: 'Announce', n: unread.broadcast || 0 },
    { key: 'campaign', label: ADMIN_APP ? 'Campaign' : (campaignName || 'Campaign'), n: campUnread },
    { key: 'dm', label: 'Direct', n: dmUnread }
  ];
  const canType = (tab === 'broadcast' && isAdmin) || (tab === 'campaign' && !!campId) || (tab === 'dm' && !!dmUser);
  const pending = outbox.filter(x => x.view === viewKey);
  const lastMineId = (() => { for (let i = messages.length - 1; i >= 0; i--) if (messages[i].sender_id === myId) return messages[i].id; return 0; })();

  // The IT / TECH account (display name "IT / TECH") is pinned at the top of
  // Direct for everyone, with a TECH badge, so agents always find support.
  const isTech = u => /^\s*IT\s*\/\s*TECH\s*$/i.test(u.name || '');
  const contacts = [...users]
    .filter(u => !search.trim() || u.name.toLowerCase().includes(search.trim().toLowerCase()) || (isTech(u) && 'tech support it'.includes(search.trim().toLowerCase())))
    .sort((a, b) => (Number(isTech(b)) - Number(isTech(a)))
      || ((unread.dms[b.id] || b.unread || 0) > 0) - ((unread.dms[a.id] || a.unread || 0) > 0)
      || (new Date(b.last_at || 0) - new Date(a.last_at || 0))
      || (Number(!!b.online) - Number(!!a.online))
      || a.name.localeCompare(b.name));

  const Pill = ({ n, light }) => (n > 0 ? (
    <span style={{ minWidth: 17, height: 17, padding: '0 5px', borderRadius: 9, background: light ? '#fff' : RED, color: light ? RED : '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>{n > 99 ? '99+' : n}</span>
  ) : null);

  let lastDay = null;

  return (
    <>
      <style>{`@keyframes chatShake{0%,100%{transform:translateX(0) rotate(0)}15%{transform:translateX(-3px) rotate(-7deg)}30%{transform:translateX(3px) rotate(7deg)}45%{transform:translateX(-3px) rotate(-5deg)}60%{transform:translateX(3px) rotate(5deg)}75%{transform:translateX(-2px) rotate(-2deg)}}@keyframes chatBadgePop{0%{transform:scale(0.4)}60%{transform:scale(1.25)}100%{transform:scale(1)}}@keyframes chatPopIn{from{transform:translateY(12px) scale(.96);opacity:0}to{transform:none;opacity:1}}.chat-bubble-row .chat-del{opacity:0}.chat-bubble-row:hover .chat-del{opacity:1}`}</style>

      {/* Priority pop-ups (agents): stay on screen until dismissed */}
      {popups.length > 0 && (
        <div style={{ position: 'fixed', right: 20, bottom: 86, zIndex: 10000, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 340 }}>
          {popups.map(p => (
            <div key={p.id} style={{ width: 330, background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 22px 60px rgba(11,27,58,0.4)', border: `2px solid ${RED}`, animation: 'chatPopIn 0.25s ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: RED, color: '#fff' }}>
                <span style={{ fontSize: 15 }}>⚠</span>
                <div style={{ flex: 1, fontWeight: 800, fontSize: 12 }}>Priority — {p.sender_name} <span style={{ opacity: 0.85, fontWeight: 600 }}>· {p.channel === 'dm' ? 'Direct' : p.channel === 'campaign' ? 'Campaign' : 'Announce'}</span></div>
                <button onClick={() => setPopups(list => list.filter(x => x.id !== p.id))} style={{ background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: 8, width: 24, height: 24, color: '#fff', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}>×</button>
              </div>
              <div onClick={() => openInChat(p)} style={{ padding: '12px 14px', color: TEXT, fontSize: 14, lineHeight: 1.45, cursor: 'pointer', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {p.body}
                {p.attachment_url && p.attachment_type === 'image' && (
                  <a href={p.attachment_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}><img src={p.attachment_url} alt={p.attachment_name} style={{ maxWidth: '100%', borderRadius: 8, marginTop: 8, display: 'block' }} /></a>
                )}
                {p.attachment_url && p.attachment_type !== 'image' && (
                  <a href={p.attachment_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ display: 'inline-block', marginTop: 8, color: ACCENT, fontWeight: 700 }}>📎 {p.attachment_name}</a>
                )}
              </div>
              <button onClick={() => openInChat(p)} style={{ width: '100%', border: 'none', borderTop: '1px solid #fee2e2', background: '#fff5f5', color: RED, fontWeight: 800, fontSize: 12, padding: '9px 0', cursor: 'pointer' }}>Open in chat ›</button>
            </div>
          ))}
        </div>
      )}

      {/* Who just wrote, while the panel is closed. Click to jump straight to it. */}
      {!open && toasts.length > 0 && (
        <div style={{ position: 'fixed', right: popups.length ? 372 : 20, bottom: 86, zIndex: 9995, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {toasts.map(t => (
            <div key={t.id} data-chat-toast={t.sender_id} onClick={() => openInChat(t)} title="Open this conversation"
              style={{ width: 300, background: '#fff', border: `1px solid ${BORDER}`, borderLeft: `4px solid ${ACCENT}`, borderRadius: 12, boxShadow: '0 12px 32px rgba(11,27,58,0.18)', padding: '10px 12px', cursor: 'pointer', animation: 'chatPopIn 0.25s ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0, color: TEXT, fontSize: 12.5, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.sender_name}</span>
                <span style={{ color: ACCENT, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
                  {t.channel === 'dm' ? 'Direct' : t.channel === 'campaign' ? ((campList.find(c => String(c.id) === String(t.campaign_id)) || {}).name || campaignName || 'Campaign') : 'Announcement'}
                </span>
                <button onClick={e => { e.stopPropagation(); setToasts(l => l.filter(x => x.id !== t.id)); }} title="Dismiss" style={{ background: 'none', border: 'none', color: TEXT2, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
              </div>
              <div style={{ color: TEXT2, fontSize: 12, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.body || (t.attachment_name ? `Attachment: ${t.attachment_name}` : '')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Named unread list beside the launcher (panel closed, no card showing) */}
      {!open && toasts.length === 0 && whereUnread.length > 0 && (
        <div data-chat-where style={{ position: 'fixed', right: 84, bottom: 22, zIndex: 9990, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, maxWidth: 'calc(100vw - 110px)' }}>
          {whereUnread.slice(0, 4).map(w => (
            <button key={w.key} data-chat-where-item={w.key} onClick={() => { w.go(); setToasts([]); setOpen(true); }} title="Open this conversation"
              style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 280, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 999, padding: '6px 8px 6px 12px', cursor: 'pointer', boxShadow: '0 8px 20px -10px rgba(11,27,58,0.45)' }}>
              <span style={{ color: ACCENT, fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>{w.kind}</span>
              <span style={{ color: TEXT, fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.name}</span>
              <Pill n={w.n} />
            </button>
          ))}
          {whereUnread.length > 4 && <span style={{ color: TEXT2, fontSize: 11, fontWeight: 700, paddingRight: 8 }}>+{whereUnread.length - 4} more</span>}
        </div>
      )}

      {/* Launcher */}
      <button onClick={toggleOpen} title={total > 0 ? `Team chat: ${whereUnread.map(w => `${w.name} (${w.n})`).join(', ')}` : 'Team chat'} data-chat-launcher style={{
        position: 'fixed', right: 20, bottom: 20, zIndex: 9990, width: 54, height: 54, borderRadius: '50%', border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg,#0284c7 0%,#2563eb 100%)', boxShadow: '0 10px 24px -6px rgba(37,99,235,0.6), 0 0 0 1px rgba(11,27,58,0.04)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', animation: shake ? 'chatShake 0.55s ease' : 'none'
      }}>
        <ChatIcon />
        {total > 0 && (
          <span key={total} data-chat-badge style={{ position: 'absolute', top: -3, right: -3, minWidth: 20, height: 20, padding: '0 5px', borderRadius: 10, background: RED, color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', animation: 'chatBadgePop 0.3s ease' }}>{total > 99 ? '99+' : total}</span>
        )}
      </button>

      {open && (
        <div data-chat-panel style={{ position: 'fixed', right: 20, bottom: 86, zIndex: 9990, width: 372, height: 540, maxHeight: 'calc(100vh - 110px)', maxWidth: 'calc(100vw - 24px)', background: PANEL_BG, border: `1px solid ${BORDER}`, borderRadius: 16, boxShadow: '0 2px 4px rgba(11,27,58,0.05), 0 24px 60px -20px rgba(11,27,58,0.35)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 28, height: 28, borderRadius: 9, background: 'linear-gradient(135deg,#0284c7 0%,#2563eb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ChatIcon size={16} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: TEXT, fontWeight: 800, fontSize: 13, lineHeight: 1 }}>Team Chat</div>
              <div style={{ color: connected ? '#16a34a' : '#d97706', fontSize: 10, marginTop: 3, fontWeight: 700 }}>{connected ? '● Connected' : '● Reconnecting…'}</div>
            </div>
            {ADMIN_APP && (
              <button onClick={() => setPriority(p => !p)} title={priority ? 'Priority ON — your messages pop up on agents\' screens' : 'Priority OFF — normal delivery'}
                style={{ display: 'flex', alignItems: 'center', gap: 3, background: priority ? 'rgba(225,29,72,0.12)' : 'none', border: `1px solid ${priority ? RED : BORDER}`, borderRadius: 8, padding: '4px 8px', color: priority ? RED : TEXT2, cursor: 'pointer', fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' }}>
                ⚠ {priority ? 'Priority' : 'Normal'}
              </button>
            )}
            <button onClick={toggleSound} title={soundOn ? 'Message sound on — click to mute' : 'Message sound off — click to turn on'}
              style={{ background: 'none', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '4px 8px', color: soundOn ? ACCENT : TEXT2, cursor: 'pointer', fontSize: 11, lineHeight: 1 }}>{soundOn ? '🔔' : '🔕'}</button>
            <button onClick={() => setOpen(false)} title="Close" style={{ background: 'none', border: 'none', color: TEXT2, fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          {/* Tabs, each with its own unread count */}
          <div style={{ display: 'flex', padding: '8px 8px 0', gap: 4 }}>
            {tabs.map(t => (
              <button key={t.key} data-chat-tab={t.key} onClick={() => { setTab(t.key); if (t.key !== 'dm') setDmUser(null); }} style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 700, background: tab === t.key ? CARD_BG : 'transparent', color: tab === t.key ? ACCENT : TEXT2, borderBottom: tab === t.key ? `2px solid ${ACCENT}` : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span><Pill n={t.n} />
              </button>
            ))}
          </div>

          {ADMIN_APP && tab === 'campaign' && (
            <div style={{ padding: '8px 10px 0' }}>
              <select value={campSel} onChange={e => setCampSel(e.target.value)} style={{ width: '100%', background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, color: TEXT, fontSize: 12, padding: '7px 8px', outline: 'none' }}>
                <option value="">— choose a campaign team —</option>
                {campList.map(c => <option key={c.id} value={c.id}>{c.name}{unread.campaigns[c.id] ? ` (${unread.campaigns[c.id]} new)` : ''}</option>)}
              </select>
            </div>
          )}

          {tab === 'dm' && !dmUser ? (
            <div ref={bodyRef} style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
              {users.length > 6 && (
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search people…" style={{ width: '100%', background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 10px', color: TEXT, fontSize: 12.5, outline: 'none', marginBottom: 8 }} />
              )}
              {contacts.length === 0 && <div style={{ color: TEXT2, fontSize: 12, padding: 10 }}>{users.length ? 'No one matches.' : 'No one available.'}</div>}
              {contacts.map(u => {
                const n = unread.dms[u.id] || 0;
                return (
                  <button key={u.id} data-chat-contact={u.id} onClick={() => { setDmUser(u); setSearch(''); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', marginBottom: isTech(u) ? 8 : 4, background: n ? '#f3ecff' : isTech(u) ? '#eef6ff' : CARD_BG, border: `1px solid ${n ? '#bfdbfe' : isTech(u) ? '#0284c7' : BORDER}`, borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}>
                    <span title={u.online ? 'Online' : 'Offline'} style={{ width: 9, height: 9, borderRadius: '50%', background: u.online ? '#16a34a' : '#c5d6ee', flexShrink: 0, boxShadow: u.online ? '0 0 0 3px rgba(22,163,74,0.18)' : 'none' }} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ color: TEXT, fontSize: 13, fontWeight: n ? 800 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                        {isTech(u)
                          ? <span style={{ background: 'linear-gradient(135deg,#0284c7,#2563eb)', color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', padding: '1px 7px', borderRadius: 999, flexShrink: 0 }}>TECH SUPPORT</span>
                          : <span style={{ color: TEXT2, fontSize: 9.5, textTransform: 'uppercase', fontWeight: 700, flexShrink: 0 }}>{u.role}</span>}
                      </span>
                      <span style={{ display: 'block', color: n ? TEXT : TEXT2, fontSize: 11.5, fontWeight: n ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                        {u.last_body != null ? `${u.last_mine ? 'You: ' : ''}${u.last_body || 'Attachment'}` : (isTech(u) ? 'Message IT / Tech for any problem' : 'No messages yet')}
                      </span>
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                      <span style={{ color: TEXT2, fontSize: 10 }}>{fmtShort(u.last_at)}</span>
                      <Pill n={n} />
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (tab === 'campaign' && !campId) ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT2, fontSize: 12, padding: 20, textAlign: 'center' }}>
              {ADMIN_APP ? 'Choose a campaign above to view and post to its team chat.' : 'Join a campaign to use its team chat.'}
            </div>
          ) : (
            <>
              {tab === 'dm' && dmUser && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 0' }}>
                  <button onClick={() => { setDmUser(null); loadUsers(); }} style={{ background: 'none', border: 'none', color: ACCENT, fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}>‹ Back</button>
                  <span data-chat-peer style={{ color: TEXT, fontSize: 12.5, fontWeight: 800 }}>{(users.find(u => u.id === dmUser.id) || dmUser).name}</span>
                </div>
              )}
              <div ref={bodyRef} data-chat-body onPaste={onPaste} style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {hasMore && <button onClick={loadEarlier} style={{ alignSelf: 'center', background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 999, padding: '4px 12px', color: ACCENT, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Load earlier messages</button>}
                {messages.length === 0 && pending.length === 0 && (
                  <div style={{ color: TEXT2, fontSize: 12, textAlign: 'center', margin: 'auto', padding: 20 }}>
                    {tab === 'broadcast' ? 'No announcements yet.' : tab === 'campaign' ? 'No messages in this campaign team yet.' : 'No messages yet. Say hello.'}
                  </div>
                )}
                {messages.map(m => {
                  const k = dayKey(m.created_at); const showDay = k !== lastDay; lastDay = k;
                  const mine = m.sender_id === myId;
                  return (
                    <React.Fragment key={m.id}>
                      {showDay && <div style={{ alignSelf: 'center', color: TEXT2, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', background: CARD_BG, borderRadius: 999, padding: '3px 10px', margin: '4px 0' }}>{dayLabel(m.created_at)}</div>}
                      <Bubble m={m} mine={mine}
                        status={mine && tab === 'dm' && m.id === lastMineId ? (m.id <= peerRead ? 'Seen' : 'Sent') : ''}
                        onDelete={isAdmin ? () => removeMessage(m) : null} />
                    </React.Fragment>
                  );
                })}
                {pending.map(e => (
                  <div key={e.cid} style={{ alignSelf: 'flex-end', maxWidth: '82%' }}>
                    <div style={{ background: ACCENT, opacity: e.state === 'failed' ? 1 : 0.6, color: '#fff', border: e.state === 'failed' ? `2px solid ${RED}` : 'none', padding: '8px 11px', borderRadius: 12, fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {e.payload.body}{e.payload.attachment ? <div style={{ marginTop: e.payload.body ? 6 : 0, fontWeight: 700 }}>📎 {e.payload.attachment.name}</div> : null}
                    </div>
                    <div style={{ fontSize: 10, marginTop: 2, textAlign: 'right', padding: '0 4px', color: e.state === 'failed' ? RED : TEXT2, fontWeight: e.state === 'failed' ? 700 : 400 }}>
                      {e.state === 'failed'
                        ? <>Not sent · <button onClick={() => retry(e)} style={{ background: 'none', border: 'none', color: ACCENT, fontWeight: 800, cursor: 'pointer', padding: 0, fontSize: 10 }}>Retry</button> · <button onClick={() => discard(e)} style={{ background: 'none', border: 'none', color: TEXT2, fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 10 }}>Remove</button></>
                        : 'Sending…'}
                    </div>
                  </div>
                ))}
              </div>

              {pendingAtt && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderTop: `1px solid ${BORDER}`, background: CARD_BG }}>
                  {pendingAtt.type === 'image' ? <img src={pendingAtt.url} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 8 }} /> : <span style={{ fontSize: 16 }}>📎</span>}
                  <span style={{ flex: 1, color: TEXT, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pendingAtt.name}</span>
                  <button onClick={() => setPendingAtt(null)} title="Remove attachment" style={{ background: 'none', border: 'none', color: TEXT2, cursor: 'pointer', fontSize: 16 }}>×</button>
                </div>
              )}

              {canType ? (
                <div style={{ padding: 10, borderTop: pendingAtt ? 'none' : `1px solid ${BORDER}`, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" style={{ display: 'none' }} onChange={e => { uploadFile(e.target.files[0]); e.target.value = ''; }} />
                  <button onClick={() => fileRef.current && fileRef.current.click()} title="Attach a file" disabled={attaching} style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, width: 38, height: 38, color: ACCENT, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>{attaching ? '…' : '📎'}</button>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button data-chat-emoji onClick={() => setEmojiOpen(o => !o)} title="Add an emoji" style={{ background: emojiOpen ? '#e8f1fe' : CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, width: 38, height: 38, cursor: 'pointer', fontSize: 17, lineHeight: 1 }}>😊</button>
                    {emojiOpen && (
                      <div style={{ position: 'absolute', bottom: 44, left: 0, zIndex: 20, width: 236, padding: 8, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, boxShadow: '0 14px 30px -12px rgba(11,27,58,0.35)', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2 }}>
                        {EMOJIS.map(e => (
                          <button key={e} onClick={() => {
                            const el = inputRef.current; const start = el ? el.selectionStart : text.length; const end = el ? el.selectionEnd : text.length;
                            const next = text.slice(0, start) + e + text.slice(end);
                            setText(next); setEmojiOpen(false);
                            setTimeout(() => { if (el) { el.focus(); const pos = start + e.length; try { el.setSelectionRange(pos, pos); } catch (_) {} } }, 0);
                          }} style={{ background: 'none', border: 'none', fontSize: 20, lineHeight: '32px', height: 34, cursor: 'pointer', borderRadius: 8 }} title={e}>{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <textarea ref={inputRef} data-chat-input value={text} rows={Math.min(4, Math.max(1, text.split('\n').length))}
                    onChange={e => setText(e.target.value)} onPaste={onPaste}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={ADMIN_APP && priority ? 'Priority message…' : 'Type a message…'}
                    style={{ flex: 1, resize: 'none', fontFamily: 'inherit', background: CARD_BG, border: `1px solid ${ADMIN_APP && priority ? RED : BORDER}`, borderRadius: 10, padding: '9px 12px', color: TEXT, fontSize: 13, lineHeight: 1.4, outline: 'none' }} />
                  <button onClick={send} data-chat-send disabled={!connected || (!text.trim() && !pendingAtt)} title={connected ? 'Send (Enter)' : 'Waiting for the connection'}
                    style={{ background: ADMIN_APP && priority ? RED : ACCENT, opacity: (!connected || (!text.trim() && !pendingAtt)) ? 0.5 : 1, border: 'none', borderRadius: 10, padding: '0 14px', height: 38, color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>Send</button>
                </div>
              ) : (
                <div style={{ padding: '12px 14px', borderTop: `1px solid ${BORDER}`, color: TEXT2, fontSize: 11.5, textAlign: 'center' }}>
                  {tab === 'broadcast' ? 'Only admins post announcements. You will see them here.' : 'Read-only.'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

function Bubble({ m, mine, status, onDelete }) {
  const isImg = m.attachment_url && m.attachment_type === 'image';
  const isFile = m.attachment_url && m.attachment_type !== 'image';
  const prio = !!m.priority;
  return (
    <div className="chat-bubble-row" data-chat-msg={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
      {(!mine || prio) && (
        <div style={{ fontSize: 10, color: prio ? RED : ACCENT, fontWeight: 800, marginBottom: 2, padding: '0 4px', textAlign: mine ? 'right' : 'left' }}>
          {!mine ? `${m.sender_name}${m.sender_role && ['admin', 'supervisor'].includes(m.sender_role) ? ' · admin' : ''}` : ''}
          {prio ? `${!mine ? ' · ' : ''}⚠ PRIORITY` : ''}
        </div>
      )}
      <div style={{ background: mine ? ACCENT : CARD_BG, color: mine ? '#fff' : TEXT, border: prio ? `2px solid ${RED}` : (mine ? 'none' : `1px solid ${BORDER}`), padding: '8px 11px', borderRadius: 12, fontSize: 13, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {m.body}
        {isImg && <a href={m.attachment_url} target="_blank" rel="noreferrer"><img src={m.attachment_url} alt={m.attachment_name} style={{ maxWidth: '100%', borderRadius: 8, marginTop: m.body ? 6 : 0, display: 'block' }} /></a>}
        {isFile && <a href={m.attachment_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: m.body ? 6 : 0, color: mine ? '#eadcff' : ACCENT, fontWeight: 700 }}>📎 {m.attachment_name}</a>}
      </div>
      <div style={{ fontSize: 10, color: TEXT2, marginTop: 2, textAlign: mine ? 'right' : 'left', padding: '0 4px' }}>
        {fmtTime(m.created_at)}{status ? <span data-chat-status style={{ color: status === 'Seen' ? '#16a34a' : TEXT2, fontWeight: 700 }}> · {status}</span> : null}
        {onDelete && <button className="chat-del" onClick={onDelete} title="Remove for everyone" style={{ background: 'none', border: 'none', color: RED, fontWeight: 700, cursor: 'pointer', padding: '0 0 0 6px', fontSize: 10 }}>Remove</button>}
      </div>
    </div>
  );
}

function ChatIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}
